// Loading states: inline Spinner, the rune (big spinner), Skeleton placeholders, the boot
// LoadingScreen, and `useSettled` — "show a skeleton for the first moment a subscription cache is
// empty, then an honest empty state".
import { useEffect, useState, type ReactNode } from 'react';
import { Display } from './Panel';
import { Meter } from './Meter';
import { deviceNow } from '../../core/time';

export function Spinner({ large, className = '', label = 'Loading' }: { large?: boolean; className?: string; label?: string }) {
  return <span className={`fk-spinner ${large ? 'fk-spinner--lg' : ''} ${className}`} role="status" aria-label={label} />;
}

export function Rune({ className = '' }: { className?: string }) {
  return <div className={`fk-rune ${className}`} aria-hidden="true"><div className="fk-rune__ring" /><div className="fk-rune__ring fk-rune__ring--inner" /><div className="fk-rune__core" /></div>;
}

export function Skeleton({ kind = 'text', width, className = '', style }: { kind?: 'text' | 'title' | 'card' | 'circle'; width?: string; className?: string; style?: React.CSSProperties }) {
  return <span className={`fk-skeleton fk-skeleton--${kind} ${className}`} style={{ width, ...style }} aria-hidden="true" />;
}

/** `n` card-shaped placeholders in a column. */
export function SkeletonList({ n = 3, kind = 'card', label = 'Loading' }: { n?: number; kind?: 'card' | 'text'; label?: string }) {
  return <div className="fk-skeleton-stack" role="status" aria-label={label} aria-busy="true">{Array.from({ length: n }, (_, i) => <Skeleton key={i} kind={kind} />)}</div>;
}

/** True once `ms` have passed since mount (or since `key` changed). Pair with an empty list: skeletons before, EmptyState after. */
export function useSettled(ms = 1200, key?: unknown): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => { setSettled(false); const t = setTimeout(() => setSettled(true), ms); return () => clearTimeout(t); }, [ms, key]);
  return settled;
}

export interface LoadingPhase { id: string; label: string }

const TIPS = [
  'Every number in Fellowkeep is exact. The preview shows what will happen, not what might.',
  'Action 1 resolves for everyone before Action 2. Order only shows when it changes a result.',
  'A goblin that watched you fight remembers. The ledger keeps the grudge.',
  'Pause is a courtesy to the party, not a weapon against the clock.',
  'The storyteller never rolls dice. The forge did that long before you arrived.',
  'Fleeing is a plan like any other. Call it early and reach the western edge together.',
];

/** Full-screen boot state: wordmark, rune, the phase list with the current one lit, a drifting tip. */
export function LoadingScreen({ phases, current, title = 'Fellowkeep', subtitle, error }: { phases: ReadonlyArray<LoadingPhase>; current: string; title?: string; subtitle?: string; error?: ReactNode }) {
  const idx = phases.findIndex((p) => p.id === current);
  const [tip, setTip] = useState(() => Math.floor((deviceNow() / 1000) % TIPS.length));
  useEffect(() => { const t = setInterval(() => setTip((i) => (i + 1) % TIPS.length), 6000); return () => clearInterval(t); }, []);
  return (
    <div className="fk-loading fk-hit" role="status" aria-live="polite" data-testid="loading-screen" data-phase={current}>
      <div className="fk-loading__inner">
        <Display size="xl" gold>{title}</Display>
        {subtitle && <div className="fk-muted" style={{ marginTop: '-0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.75rem' }}>{subtitle}</div>}
        <Rune />
        <ul className="fk-loading__phases">
          {phases.map((p, i) => {
            const state = i < idx ? 'done' : i === idx ? 'active' : 'todo';
            return <li key={p.id} className={`fk-loading__phase fk-loading__phase--${state}`}><span className="fk-loading__phase-mark" aria-hidden="true">{state === 'done' ? '✓' : state === 'active' ? '›' : '·'}</span>{p.label}{state === 'active' && <Spinner className="fk-faint" label="" />}</li>;
          })}
        </ul>
        <Meter value={Math.max(0, idx)} max={Math.max(1, phases.length)} showValue={false} indeterminate={idx >= 0} aria-label="Loading progress" />
        {error ? <div className="fk-loading__tip" style={{ color: 'var(--fk-red)' }}>{error}</div> : <div key={tip} className="fk-loading__tip fk-reveal fk-reveal--fade">{TIPS[tip]}</div>}
      </div>
    </div>
  );
}
